/**
 * ProductLiveDemo — interactive "Example in action" widgets shown on each
 * product checkout page. Buyers can drag, click and step through a real
 * mini-version of the tool BEFORE they buy so they feel the payoff.
 *
 * Every demo is fully self-contained: no backend calls, math runs in the
 * browser with hard-coded seed data. The tool they get after purchase is
 * the full-power version that runs on THEIR numbers, not a demo.
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Sparkles, DollarSign, TrendingDown, MessageSquare, Package, Award,
  CheckCircle2, Calculator, Search, Gavel, Scale, Crown, PhoneCall,
  Send, ChevronRight,
} from 'lucide-react';

/* --------------------------- Shell + primitives --------------------------- */

const DemoShell = ({ title, subtitle, badge = 'Try it live', children, footer }) => (
  <div
    className="mt-6 rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 via-cyan-500/5 to-transparent p-4 sm:p-5"
    data-testid="product-live-demo"
  >
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-black uppercase tracking-widest">
        <Sparkles size={10} /> {badge}
      </span>
      <p className="text-xs text-slate-400">{subtitle}</p>
    </div>
    <h3 className="text-sm sm:text-base font-bold text-white mb-3">{title}</h3>
    {children}
    <p className="text-[11px] text-slate-500 mt-3 italic">
      {footer || "This is a sample. Buy below and run it on your own numbers."}
    </p>
  </div>
);

const money = (n) => {
  if (!Number.isFinite(n)) return '$0';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(Math.round(n));
  return `${sign}$${v.toLocaleString('en-US')}`;
};

const Slider = ({ label, min, max, step, value, onChange, format, accent = 'amber' }) => (
  <label className="block">
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="text-slate-300 font-semibold">{label}</span>
      <span className={`font-mono font-black ${accent === 'amber' ? 'text-amber-300' : accent === 'cyan' ? 'text-cyan-300' : 'text-emerald-300'}`}>
        {format ? format(value) : value}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className={`w-full h-1.5 rounded-full appearance-none cursor-pointer bg-white/10 ${
        accent === 'amber'
          ? 'accent-amber-400'
          : accent === 'cyan'
          ? 'accent-cyan-400'
          : 'accent-emerald-400'
      }`}
    />
  </label>
);

const Stat = ({ label, value, accent, big }) => (
  <div className="rounded-lg bg-black/30 p-2.5">
    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
    <p className={`${big ? 'text-2xl' : 'text-base'} font-black font-mono mt-0.5 ${accent || 'text-white'}`}>{value}</p>
  </div>
);

/* --------------------------- RV vs RV Comparison ------------------------ */

const RV_OPTIONS = [
  { id: 'micro',   label: '2024 Winnebago Micro Minnie',   price: 32995, length: 21, sleeps: 4, dry: 3480, mpg: 12,  cargo: 800,  offGrid: 1, feature: 'Best for weekend couples' },
  { id: 'jayfea',  label: '2024 Jayco Jay Feather',         price: 38500, length: 24, sleeps: 5, dry: 5100, mpg: 10,  cargo: 1200, offGrid: 2, feature: 'Balanced family hauler' },
  { id: 'imagine', label: '2024 Grand Design Imagine',      price: 44900, length: 28, sleeps: 6, dry: 6800, mpg: 9,   cargo: 1600, offGrid: 3, feature: 'Long-trip comfort' },
  { id: 'santara', label: '2024 Coachmen Santara Rebel',    price: 42500, length: 26, sleeps: 4, dry: 5900, mpg: 9,   cargo: 1400, offGrid: 4, feature: 'Off-grid ready' },
];

const CompareRow = ({ label, aVal, bVal, format, betterIsLower = false }) => {
  const a = Number(aVal);
  const b = Number(bVal);
  const aBetter = betterIsLower ? a < b : a > b;
  const bBetter = betterIsLower ? b < a : b > a;
  const cell = (val, better) => (
    <div className={`text-center text-xs font-mono py-1 rounded ${better ? 'bg-emerald-500/20 text-emerald-300 font-black' : 'text-slate-300'}`}>
      {format ? format(val) : val}
    </div>
  );
  return (
    <div className="grid grid-cols-[80px_1fr_1fr] gap-2 items-center">
      <p className="text-[11px] text-slate-400 font-semibold">{label}</p>
      {cell(aVal, aBetter)}
      {cell(bVal, bBetter)}
    </div>
  );
};

const RVCompareDemo = () => {
  const [aId, setAId] = useState('micro');
  const [bId, setBId] = useState('imagine');
  const a = RV_OPTIONS.find((r) => r.id === aId);
  const b = RV_OPTIONS.find((r) => r.id === bId);

  const dropdown = (value, onChange, testid) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-black/40 border border-white/10 text-xs text-white font-bold rounded-lg px-2 py-1.5 cursor-pointer hover:border-cyan-400/40"
      data-testid={testid}
    >
      {RV_OPTIONS.map((r) => (
        <option key={r.id} value={r.id}>{r.label}</option>
      ))}
    </select>
  );

  return (
    <DemoShell
      title="Head-to-head, side-by-side"
      subtitle="RV Comparison — pick any two rigs from the dropdowns"
    >
      <div className="grid grid-cols-2 gap-2 mb-3">
        {dropdown(aId, setAId, 'compare-rv-a')}
        {dropdown(bId, setBId, 'compare-rv-b')}
      </div>
      {a && b && (
        <div className="space-y-1.5 rounded-lg bg-black/30 p-3">
          <CompareRow label="Price"     aVal={a.price}   bVal={b.price}   format={money} betterIsLower />
          <CompareRow label="Length"    aVal={a.length}  bVal={b.length}  format={(v) => `${v} ft`} />
          <CompareRow label="Sleeps"    aVal={a.sleeps}  bVal={b.sleeps} />
          <CompareRow label="Dry wt"    aVal={a.dry}     bVal={b.dry}     format={(v) => `${v.toLocaleString()} lb`} betterIsLower />
          <CompareRow label="Cargo"     aVal={a.cargo}   bVal={b.cargo}   format={(v) => `${v.toLocaleString()} lb`} />
          <CompareRow label="Tow MPG"   aVal={a.mpg}     bVal={b.mpg} />
          <CompareRow label="Off-grid"  aVal={a.offGrid} bVal={b.offGrid} format={(v) => '★'.repeat(v)} />
        </div>
      )}
      {a && b && a.id !== b.id && (
        <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-2.5 text-xs text-slate-200">
          <Scale className="inline w-3.5 h-3.5 text-emerald-300 mr-1" />
          <strong>{a.price < b.price ? a.label.split(' ')[2] : b.label.split(' ')[2]}</strong> wins on price by <strong>{money(Math.abs(a.price - b.price))}</strong>. The real tool ranks 8+ dimensions, cross-references your tow rig, and calls out the honest compromise on each side.
        </div>
      )}
    </DemoShell>
  );
};

/* --------------------------- Cost Calculator ---------------------------- */

const CostCalculatorDemo = () => {
  const [price, setPrice] = useState(32995);
  const [down, setDown] = useState(5000);
  const [term, setTerm] = useState(180);   // months
  const rate = 0.0799;                     // fixed for demo

  const { monthly, allIn, totalInterest } = useMemo(() => {
    const loan = Math.max(0, price - down);
    const r = rate / 12;
    const m = loan === 0 ? 0 : (loan * r) / (1 - Math.pow(1 + r, -term));
    const paid = m * term;
    const insurance = 88;
    const storage = 95;
    return {
      monthly: m,
      allIn: m + insurance + storage,
      totalInterest: paid - loan,
    };
  }, [price, down, term]);

  return (
    <DemoShell
      title="2024 Winnebago Micro Minnie · 7.99% APR"
      subtitle="Cost Calculator — drag any slider"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Slider label="Purchase Price" min={15000} max={90000} step={500} value={price} onChange={setPrice} format={money} />
          <Slider label="Down Payment"   min={0}     max={30000} step={500} value={down}  onChange={setDown}  format={money} />
          <Slider label="Loan Term"      min={60}    max={240}   step={12}  value={term}  onChange={setTerm}  format={(v) => `${v / 12} yrs`} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Loan Payment"     value={money(monthly)}                    accent="text-emerald-300" big />
          <Stat label="All-in / month"   value={money(allIn)}                      accent="text-amber-300"   big />
          <Stat label="Interest Total"   value={money(totalInterest)}              accent="text-rose-300" />
          <Stat label="Loan Balance"     value={money(Math.max(0, price - down))}  accent="text-white" />
        </div>
      </div>
      <div className="mt-3 text-xs text-slate-300 leading-snug rounded-lg bg-white/[0.03] p-2.5">
        <DollarSign className="inline w-3 h-3 text-emerald-400 mr-1" />
        Cutting your term to <strong>10 years</strong> (drag the slider) trims total interest dramatically — the full tool models insurance, maintenance and storage by rig type and zip code.
      </div>
    </DemoShell>
  );
};

/* ----------------------------- Deal Analyzer ---------------------------- */

const DealAnalyzerDemo = () => {
  const msrp = 44900;
  const marketAvg = 37900;
  const [offer, setOffer] = useState(38500);

  const analysis = useMemo(() => {
    const discount = ((msrp - offer) / msrp) * 100;
    const vsMarket = ((marketAvg - offer) / marketAvg) * 100;
    let grade, tone;
    if (offer <= marketAvg - 2000) { grade = 'A+'; tone = 'text-emerald-300 bg-emerald-500/15'; }
    else if (offer <= marketAvg)   { grade = 'A';  tone = 'text-emerald-300 bg-emerald-500/15'; }
    else if (offer <= marketAvg + 1500) { grade = 'B+'; tone = 'text-lime-300    bg-lime-500/15'; }
    else if (offer <= marketAvg + 3500) { grade = 'B';  tone = 'text-amber-300   bg-amber-500/15'; }
    else if (offer <= msrp * 0.95)      { grade = 'C';  tone = 'text-orange-300  bg-orange-500/15'; }
    else                                { grade = 'D';  tone = 'text-rose-300    bg-rose-500/15'; }
    return { grade, tone, discount, vsMarket };
  }, [offer]);

  return (
    <DemoShell
      title="2024 Grand Design Reflection · MSRP $44,900"
      subtitle="Deal Analyzer — drag the offer to re-grade instantly"
    >
      <div className="mb-3">
        <Slider label="Dealer Offer" min={30000} max={msrp} step={100} value={offer} onChange={setOffer} format={money} accent="cyan" />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className={`text-center rounded-lg p-3 ${analysis.tone}`}>
          <p className="text-[10px] uppercase tracking-wider font-bold">Fair Score</p>
          <p className="text-3xl font-black">{analysis.grade}</p>
        </div>
        <Stat label="Off MSRP"    value={`${analysis.discount.toFixed(1)}%`} accent="text-white" big />
        <Stat label="vs Market"   value={`${analysis.vsMarket >= 0 ? '−' : '+'}${Math.abs(analysis.vsMarket).toFixed(1)}%`} accent={analysis.vsMarket >= 0 ? 'text-emerald-300' : 'text-rose-300'} big />
      </div>
      <div className="text-xs text-slate-300 leading-snug rounded-lg bg-white/[0.03] p-2.5">
        <TrendingDown className="inline w-3 h-3 text-amber-400 mr-1" />
        Market average for this floorplan in the last 60 days: <strong>{money(marketAvg)}</strong>. The full tool pulls VIN-level comparables, incentive stacking, and dealer margin data from your zip code.
      </div>
    </DemoShell>
  );
};

/* --------------------------- Negotiation Coach -------------------------- */

const NEGO_STEPS = [
  {
    stage: 'Opener',
    say:  "I've done my homework and I'm ready to close today at the right number. What's your best out-the-door price including all fees?",
    avoid:"What's your best price?",
    tip:  "Anchor by asking for OUT-THE-DOOR — kills mystery fees at the finance office.",
  },
  {
    stage: 'Counter',
    say:  "That's higher than the market average I'm seeing on comparable units. I can move today at $37,200 all-in.",
    avoid:"I need to think about it.",
    tip:  "Give ONE specific number and one specific timeline. Reduces back-and-forth.",
  },
  {
    stage: 'Hold',
    say:  "I hear you — that's my walk-away. I'd rather buy it here if we can match the number, but I understand if the deal isn't there.",
    avoid:"Fine, meet me in the middle?",
    tip:  "Splitting the difference costs you money. Hold your number for at least 30 seconds of silence.",
  },
  {
    stage: 'Close',
    say:  "Deal. Print the paperwork at $37,200 out-the-door and I'll sign right now — no add-ons.",
    avoid:"What extended warranty options do you have?",
    tip:  "Say ZERO about add-ons until pen hits paper on the vehicle price.",
  },
];

const NegotiationCoachDemo = () => {
  const [i, setI] = useState(0);
  const step = NEGO_STEPS[i];
  return (
    <DemoShell
      title="Step through a real counter-offer script"
      subtitle="Negotiation Coach — click each stage"
    >
      <div className="flex gap-2 mb-3 flex-wrap">
        {NEGO_STEPS.map((s, idx) => (
          <button
            key={s.stage}
            type="button"
            onClick={() => setI(idx)}
            className={`px-3 py-1 rounded-full text-xs font-black transition-colors ${
              i === idx ? 'bg-amber-400 text-black' : 'bg-white/[0.05] text-slate-300 hover:bg-white/[0.1]'
            }`}
            data-testid={`nego-step-${idx}`}
          >
            {idx + 1}. {s.stage}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        <div className="rounded-lg bg-black/40 p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1">Say this</p>
          <p className="text-xs text-slate-200 italic">&ldquo;{step.say}&rdquo;</p>
        </div>
        <div className="rounded-lg bg-black/40 p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-rose-300 font-bold mb-1">Avoid this</p>
          <p className="text-xs text-slate-200 italic">&ldquo;{step.avoid}&rdquo;</p>
        </div>
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-2.5">
          <MessageSquare className="inline w-3 h-3 text-emerald-300 mr-1" />
          <span className="text-xs text-slate-200"><strong>Why it works:</strong> {step.tip}</span>
        </div>
      </div>
    </DemoShell>
  );
};

/* ------------------------------ Buyer Toolkit --------------------------- */

const TOOLKIT_TABS = [
  { id: 'cost',   label: 'Cost Calc',   icon: Calculator, component: CostCalculatorDemo },
  { id: 'deal',   label: 'Deal Analyzer', icon: Search,   component: DealAnalyzerDemo },
  { id: 'nego',   label: 'Nego Coach',  icon: Gavel,      component: NegotiationCoachDemo },
];

const BuyerToolkitDemo = () => {
  const [tab, setTab] = useState('cost');
  const Active = TOOLKIT_TABS.find((t) => t.id === tab)?.component || CostCalculatorDemo;
  return (
    <div className="mt-6" data-testid="product-live-demo">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-black uppercase tracking-widest">
          <Sparkles size={10} /> Try any tool live
        </span>
        <p className="text-xs text-slate-400">All 4 tools are included in the bundle</p>
      </div>
      <div className="flex gap-2 mb-1 flex-wrap">
        {TOOLKIT_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black transition-colors ${
                tab === t.id ? 'bg-cyan-400 text-black' : 'bg-white/[0.05] text-slate-300 hover:bg-white/[0.1]'
              }`}
              data-testid={`toolkit-tab-${t.id}`}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/[0.03] text-slate-500 border border-white/5 cursor-default">
          <Scale size={12} /> RV Compare <span className="text-[10px] uppercase tracking-widest">included</span>
        </span>
      </div>
      <Active />
      <div className="mt-3 text-xs text-slate-300 leading-snug rounded-lg bg-white/[0.03] p-3">
        <Package className="inline w-3.5 h-3.5 text-cyan-400 mr-1" />
        Buy the bundle once — every buyer tool lives in your dashboard for life.
      </div>
    </div>
  );
};

/* ---------------------------- VIP Buying Plan --------------------------- */

const VIP_PHASES = [
  { day: 'Day 1-3',   title: 'Advisor Kickoff Call',   detail: '30-min call: nail budget, RV type, deal-breakers. Your advisor writes your search brief.' },
  { day: 'Day 4-10',  title: 'Hand-picked Matches',    detail: 'We source 3 units nationwide with pricing intel from dealers we track.' },
  { day: 'Day 11-14', title: 'Deep-Dive Analysis',     detail: 'Deal Analyzer + Negotiation Coach run on the top pick. You get exact scripts.' },
  { day: 'Day 15-21', title: 'Advisor Joins Your Call',detail: "We're on the phone with the dealer with you. Close on your number." },
];

const VIPBuyingPlanDemo = () => {
  const [i, setI] = useState(0);
  const phase = VIP_PHASES[i];
  return (
    <DemoShell
      title="Your 21-day path to keys in hand"
      subtitle="VIP Buying Plan — click any phase"
      footer="Every VIP is booked into this exact program on day one."
    >
      <div className="grid grid-cols-4 gap-1.5 mb-4">
        {VIP_PHASES.map((p, idx) => (
          <button
            key={p.day}
            type="button"
            onClick={() => setI(idx)}
            className={`px-2 py-2 rounded-lg text-[11px] font-black transition-colors ${
              i === idx ? 'bg-fuchsia-500/25 text-fuchsia-200 border border-fuchsia-400/40' : 'bg-white/[0.03] text-slate-400 hover:bg-white/[0.06] border border-white/5'
            }`}
            data-testid={`vip-phase-${idx}`}
          >
            {p.day}
          </button>
        ))}
      </div>
      <div className="rounded-lg bg-black/30 p-3">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-4 h-4 text-fuchsia-300" />
          <p className="text-[11px] uppercase tracking-widest text-fuchsia-300 font-black">{phase.day}</p>
        </div>
        <h4 className="text-sm font-black text-white mb-1">{phase.title}</h4>
        <p className="text-xs text-slate-300 leading-relaxed">{phase.detail}</p>
      </div>
      <div className="mt-3 text-xs text-slate-300 leading-snug rounded-lg bg-white/[0.03] p-2.5">
        <Award className="inline w-3 h-3 text-amber-300 mr-1" />
        VIP buyers save an average of <strong>$2,800</strong> at the dealership vs. going it alone.
      </div>
    </DemoShell>
  );
};

/* ----------------------------- VIP Concierge ---------------------------- */

const CONCIERGE_SCRIPT = [
  { from: 'me',      text: "Found a 2024 Micro Minnie at Blue Compass — listed $38,500. Their finance guy is pushing an $2,995 extended warranty. Worth it?" },
  { from: 'advisor', text: "Nice find! On that unit, warranty coverage from Wholesale Warranties runs about $1,600 for the same 5-yr term. I'll email you a comparison in 10." },
  { from: 'me',      text: "Perfect. Also — they won't budge below $38,500. Where should I push?" },
  { from: 'advisor', text: "That unit sold twice in TX for $36,800 last month per our data feed. I'll draft a counter-offer script tonight. Text me a photo of their written offer when they hand it to you." },
];

const VIPConciergeDemo = () => {
  const [visible, setVisible] = useState(1);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (visible >= CONCIERGE_SCRIPT.length) return;
    const t = setTimeout(() => setVisible((v) => v + 1), 1200);
    return () => clearTimeout(t);
  }, [visible]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [visible]);

  const replay = () => setVisible(1);

  return (
    <DemoShell
      title="Text your advisor. Any time. Real replies."
      subtitle="VIP Concierge — a real dialogue from a live case"
      footer="This is a real conversation script. Buy VIP and this is your inbox."
    >
      <div ref={scrollRef} className="rounded-xl bg-black/40 p-3 space-y-2 max-h-56 overflow-y-auto">
        {CONCIERGE_SCRIPT.slice(0, visible).map((m, idx) => (
          <div
            key={idx}
            className={`flex ${m.from === 'me' ? 'justify-end' : 'justify-start'}`}
            data-testid={`concierge-msg-${idx}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-snug ${
                m.from === 'me'
                  ? 'bg-fuchsia-500/25 border border-fuchsia-400/40 text-fuchsia-50 rounded-br-md'
                  : 'bg-white/[0.06] border border-white/10 text-slate-100 rounded-bl-md'
              }`}
            >
              {m.from === 'advisor' && (
                <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-0.5 flex items-center gap-1">
                  <PhoneCall size={9} /> Your advisor
                </p>
              )}
              {m.text}
            </div>
          </div>
        ))}
        {visible < CONCIERGE_SCRIPT.length && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-white/[0.06] border border-white/10 px-3 py-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '0.15s' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] text-slate-400">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Avg advisor response: <strong className="text-emerald-300">under 15 min</strong>
        </div>
        {visible >= CONCIERGE_SCRIPT.length && (
          <button
            type="button"
            onClick={replay}
            className="text-[11px] font-black uppercase tracking-widest text-cyan-300 hover:text-white flex items-center gap-1"
            data-testid="concierge-replay"
          >
            <Send size={11} /> Replay
          </button>
        )}
      </div>
    </DemoShell>
  );
};

/* ------------------------------ Registry -------------------------------- */

const DEMOS = {
  cost_calculator:  <CostCalculatorDemo />,
  deal_analyzer:    <DealAnalyzerDemo />,
  negotiation_coach:<NegotiationCoachDemo />,
  rv_comparison:    <RVCompareDemo />,
  buyer_toolkit:    <BuyerToolkitDemo />,
  vip_buying_plan:  <VIPBuyingPlanDemo />,
  vip_concierge:    <VIPConciergeDemo />,
};

const ProductLiveDemo = ({ productId }) => DEMOS[productId] || null;

export default ProductLiveDemo;
